import { useEffect, useMemo, useState } from "react";
import { getMerchantCategories } from "../../merchant-categories/api/merchantCategory.api";
import type { MerchantCategory } from "../../merchant-categories/types/merchantCategory.types";

const categoryOptionPageSize = 100;

function flattenCategories(categories: MerchantCategory[]): MerchantCategory[] {
  return categories.flatMap((category) => [category, ...flattenCategories(category.children)]);
}

export function useMerchantBrandCategoryOptions(enabled: boolean) {
  const [categories, setCategories] = useState<MerchantCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let isActive = true;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const firstPage = await getMerchantCategories({
          page: 1,
          page_size: categoryOptionPageSize,
        });
        const pageCount = Math.ceil(firstPage.paging.total / categoryOptionPageSize);
        const remainingPages = await Promise.all(
          Array.from({ length: Math.max(pageCount - 1, 0) }, (_, pageOffset) =>
            getMerchantCategories({
              page: pageOffset + 2,
              page_size: categoryOptionPageSize,
            }),
          ),
        );
        if (isActive) {
          setCategories(
            flattenCategories([
              ...firstPage.list,
              ...remainingPages.flatMap((response) => response.list),
            ]),
          );
        }
      } catch (requestError) {
        if (!isActive) return;
        setCategories([]);
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Failed to load merchant categories",
        );
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void load();
    return () => {
      isActive = false;
    };
  }, [enabled]);

  const options = useMemo(
    () =>
      categories.map((category) => ({
        value: category.id,
        label: category.identifier
          ? `${category.identifier} — ${category.description}`
          : category.description,
      })),
    [categories],
  );

  return { options, error, isLoading };
}
